import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, LogOut, ShoppingCart, Store } from 'lucide-react';
import { useProAuth } from '@/lib/auth-context';
import { useProBranding } from '@/lib/pro-branding';
import { useCart } from '@/lib/cart-context';
import { useFlyToCart } from '@/lib/fly-to-cart';
import { useProCatalog } from '@/hooks/use-pro-catalog';
import { CLIENT_MODE_ROUTES } from '@/lib/client-mode';

/**
 * En-tête du mode client : logo, bascule Boutique/Catalogue, panier, avatar.
 *
 * Volontairement dépourvu de portefeuille, de notifications et de menu compte :
 * l'écran est tourné vers le client de l'électricien. L'avatar est un simple
 * repère d'identité, pas un bouton — un client ne doit pas pouvoir atteindre
 * les paramètres ni déconnecter l'électricien.
 */
export function ClientHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { proCustomer } = useProAuth();
  const { loginLogoUrl } = useProBranding();
  const { itemCount, setDrawerOpen } = useCart();

  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const { setCartTargetRef, bumpKey } = useFlyToCart();
  useEffect(() => {
    setCartTargetRef(cartButtonRef);
  }, [setCartTargetRef]);

  const initials =
    ((proCustomer?.firstName ?? '').charAt(0) +
      (proCustomer?.lastName ?? '').charAt(0)).toUpperCase() || 'TP';

  const fullName = [proCustomer?.firstName, proCustomer?.lastName]
    .filter(Boolean)
    .join(' ');

  // Catalogue vide → pas de bascule du tout. Requête partagée avec
  // CataloguePage (même clé react-query), donc aucun appel supplémentaire.
  // Exception : si on EST déjà sur le catalogue, la bascule reste le seul
  // chemin de retour vers la boutique.
  const catalog = useProCatalog();
  const onCataloguePage = pathname.startsWith(CLIENT_MODE_ROUTES.catalogue);
  const showSwitch = (catalog.data?.length ?? 0) > 0 || onCataloguePage;

  const tabs = [
    {
      to: CLIENT_MODE_ROUTES.shop,
      icon: Store,
      label: t('pro.client_mode.shop', 'Boutique'),
      end: true,
    },
    {
      to: CLIENT_MODE_ROUTES.catalogue,
      icon: BookOpen,
      label: t('pro.client_mode.catalogue', 'Catalogue'),
      end: false,
    },
  ];

  return (
    <header
      className="px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4"
      style={{
        background: 'var(--pro-bg)',
        borderBottom: '1px solid var(--pro-line)',
      }}
    >
      {/* Logo — non cliquable : rien à atteindre en dehors des deux onglets.
          `flex-1` ici ET à droite : les deux flancs reçoivent la même largeur,
          donc la bascule tombe sur le VRAI centre de l'en-tête. Centrer la nav
          dans son propre `flex-1` ne suffisait pas — le bloc de droite étant
          plus large que le logo, l'ensemble dérivait vers la gauche.
          Largeur EXPLICITE sur le conteneur du logo : le preflight Tailwind
          pose `img { max-width: 100% }`, et dans un élément flex dimensionné
          par son contenu la contrainte devient circulaire — l'image se replie
          à 0×0. (La sidebar y échappe parce que l'`aside` a une largeur fixe.) */}
      <div className="flex-1 min-w-0 flex items-center">
        <div className="w-[112px] sm:w-[150px] shrink-0">
        {loginLogoUrl ? (
          <img
            src={loginLogoUrl}
            alt=""
            className="max-h-9 max-w-full w-auto object-contain object-left"
          />
        ) : (
          <div className="flex items-baseline gap-1">
            <span
              className="text-[20px] font-bold leading-none"
              style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
            >
              Tunelec
            </span>
            <span
              className="text-[20px] font-semibold italic leading-none"
              style={{ fontFamily: 'Figtree', color: 'var(--pro-primary)' }}
            >
              Pro
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Bascule Boutique / Catalogue — pas de bascule quand il n'y a rien à
          basculer : un catalogue vide ne s'annonce pas au client. On la garde
          malgré tout si on EST sur le catalogue, sinon plus rien ne ramène à
          la boutique. */}
      {showSwitch && (
      <nav
        className="shrink-0"
        aria-label={t('pro.client_mode.nav', 'Navigation')}
      >
        <div
          className="inline-flex items-center gap-1 p-1 rounded-2xl bg-white"
          style={{ border: '1px solid var(--pro-border)' }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className="inline-flex items-center gap-2 h-9 px-3 sm:px-4 rounded-xl text-[13px] font-semibold transition-colors whitespace-nowrap"
                style={({ isActive }) =>
                  isActive
                    ? {
                        background: 'var(--pro-primary)',
                        color: '#fff',
                        fontFamily: 'Figtree',
                      }
                    : {
                        color: 'var(--pro-text-muted)',
                        fontFamily: 'Figtree',
                      }
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      )}

      <div className="flex-1 min-w-0 flex items-center justify-end gap-2 sm:gap-3">
        {/* Sortie du mode client — visible, jamais enfouie dans un menu.
            Libellé court à l'écran, intitulé complet pour les lecteurs
            d'écran : « Quitter » seul ne dit pas quoi. */}
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-white text-[13px] font-semibold hover:bg-neutral-50 transition-colors shrink-0"
          style={{
            border: '1px solid var(--pro-border)',
            color: 'var(--pro-text-dark)',
            fontFamily: 'Figtree',
          }}
          aria-label={t('pro.client_mode.exit_full', 'Quitter le mode client')}
          title={t('pro.client_mode.exit_full', 'Quitter le mode client')}
        >
          <LogOut className="w-4 h-4 rotate-180" />
          <span className="hidden sm:inline">
            {t('pro.client_mode.exit', 'Quitter')}
          </span>
        </button>

        {/* Panier */}
        <button
          ref={cartButtonRef}
          type="button"
          aria-label={t('pro.header.cart', 'Panier')}
          onClick={() => setDrawerOpen(true)}
          className="relative w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors"
        >
          <ShoppingCart
            className="w-5 h-5"
            style={{ color: 'var(--pro-text-dark)' }}
          />
          {itemCount > 0 && (
            <span
              key={bumpKey}
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white pro-bump"
              style={{ background: 'var(--pro-primary)' }}
            >
              {itemCount}
            </span>
          )}
        </button>

        {/* Avatar seul — pas de nom : le client n'a pas besoin de lire
            l'identité de l'électricien sur son propre écran. Le nom reste en
            `title` pour le survol. */}
        <span
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-[14px] font-semibold overflow-hidden shrink-0"
          style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          title={fullName}
        >
          {proCustomer?.profilePictureUrl ? (
            <img
              src={proCustomer.profilePictureUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            initials
          )}
        </span>
      </div>
    </header>
  );
}
